import { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface EditorialSectionProps {
  eyebrow?: string;
  title: ReactNode;
  intro?: ReactNode;
  id?: string;
  children: ReactNode;
  className?: string;
  divider?: boolean;
  tinted?: boolean;
}

/**
 * Luminous Atrium section wrapper:
 * - Cream/mist surface with optional tinted variant
 * - Soft sun-glow ambient backdrop
 * - Pill eyebrow + Instrument Sans medium headline
 */
export const EditorialSection = ({
  eyebrow,
  title,
  intro,
  id,
  children,
  className,
  divider = false,
  tinted = false,
}: EditorialSectionProps) => {
  return (
    <section
      id={id}
      className={cn(
        "relative px-4 sm:px-6 md:px-10 overflow-hidden",
        tinted ? "bg-secondary/40" : "bg-background",
        divider ? "border-t border-border/40" : "",
        className,
      )}
    >
      {/* Atrium ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-0 opacity-70"
        style={{
          background: tinted
            ? "radial-gradient(800px 420px at 90% 0%, hsl(50 60% 92% / 0.55), transparent 60%), radial-gradient(700px 420px at 0% 100%, hsl(150 30% 90% / 0.5), transparent 60%)"
            : "radial-gradient(700px 380px at 100% 10%, hsl(50 60% 92% / 0.4), transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto py-16 sm:py-20 md:py-28">
        <div className="max-w-3xl mb-10 sm:mb-14 md:mb-20">
          {eyebrow && (
            <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/70 backdrop-blur px-3.5 py-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-medium text-foreground/70 mb-5 md:mb-7 shadow-[0_2px_8px_-4px_rgba(21,34,27,0.06)]">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              {eyebrow}
            </span>
          )}
          <h2 className="font-sans text-[30px] sm:text-4xl md:text-5xl lg:text-[56px] leading-[1.08] tracking-[-0.02em] text-foreground font-medium mb-4 md:mb-6">
            {title}
          </h2>
          {intro && (
            <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              {intro}
            </p>
          )}
        </div>
        {children}
      </div>
    </section>
  );
};
